/**
 * Minimal EPUB CFI support for anchors written by the previous NubeReader (Readium SDK), which
 * stored a content-document CFI such as `/4/2/14/1:120` or a range `/4/2/14,/1:0,/1:25` where
 * the new reader stores a Readium progression. Only what that SDK emitted is handled: child
 * steps from the root element, optional `[id]` assertions, a terminal `:offset`, the
 * `parent,start,end` range form and an optional `epubcfi(…!…)` package prefix.
 *
 * An `[id]` assertion wins over the child index before it, as the CFI spec intends: that SDK
 * parsed chapters as HTML, where a self-closing `<header/>` swallows its next siblings, so its
 * child indexes do not always match the XHTML tree this reader builds.
 */

const CFI_START = /^(?:epubcfi\()?\//;
const CFI_WRAPPER = /^epubcfi\((.*)\)$/;
const CFI_PATH = /^((?:\/\d+(?:\[[^\]]*\])?)+)(?::(\d+))?(?:\[[^\]]*\])?$/;
const CFI_STEP = /\/(\d+)(?:\[([^\]]*)\])?/g;
const STEPS_GROUP = 1;
const OFFSET_GROUP = 2;
const RANGE_PARTS = 3;
const ELEMENT_STEP_DIVISOR = 2;

interface CfiStep {
  index: number;
  id?: string;
}

interface CfiPath {
  steps: CfiStep[];
  offset?: number;
}

interface CfiPoint {
  node: Node;
  offset: number;
}

// Node-type checks instead of `instanceof`: chapter documents live in iframes (other realms).
function isText(node: Node): node is Text {
  return node.nodeType === Node.TEXT_NODE;
}

function isElement(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE;
}

function contentPart(cfi: string): string {
  const unwrapped = CFI_WRAPPER.exec(cfi)?.[1] ?? cfi;
  const indirection = unwrapped.lastIndexOf('!');
  return indirection === -1 ? unwrapped : unwrapped.slice(indirection + 1);
}

function parsePath(path: string): CfiPath | undefined {
  const match = CFI_PATH.exec(path);
  if (!match) return undefined;
  // `.at()` types the optional capture groups as possibly undefined (indexing does not).
  const stepsPart = match.at(STEPS_GROUP);
  const offsetPart = match.at(OFFSET_GROUP);
  if (stepsPart === undefined) return undefined;
  const steps = Array.from(stepsPart.matchAll(CFI_STEP), ([, step, id]) => ({
    index: Number(step),
    id: id || undefined,
  }));
  return { steps, offset: offsetPart === undefined ? undefined : Number(offsetPart) };
}

/** Text nodes making up the odd-indexed chunk `step` of `parent` (between two element children). */
function textPoint(parent: Node, step: number, offset: number): CfiPoint | undefined {
  const chunk = (step - 1) / ELEMENT_STEP_DIVISOR;
  let elementsSeen = 0;
  const texts: Text[] = [];
  for (const child of Array.from(parent.childNodes)) {
    if (isElement(child)) {
      elementsSeen += 1;
    } else if (isText(child) && elementsSeen === chunk) {
      texts.push(child);
    }
  }

  let remaining = offset;
  for (const text of texts) {
    if (remaining <= text.length) return { node: text, offset: remaining };
    remaining -= text.length;
  }
  const last = texts.at(-1);
  return last ? { node: last, offset: last.length } : undefined;
}

function firstTextPoint(node: Node): CfiPoint | undefined {
  const doc = node.ownerDocument;
  if (!doc) return undefined;
  const walker = doc.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
    acceptNode: (text) =>
      text.nodeValue?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP,
  });
  const text = walker.nextNode();
  return text ? { node: text, offset: 0 } : undefined;
}

function resolvePath(doc: Document, path: string): CfiPoint | undefined {
  const parsed = parsePath(path);
  if (!parsed) return undefined;

  let node: Node = doc.documentElement;
  for (const [index, { index: step, id }] of parsed.steps.entries()) {
    if (step % ELEMENT_STEP_DIVISOR === 1) {
      // Odd steps address the text between element children; only valid as the last step.
      return index === parsed.steps.length - 1
        ? textPoint(node, step, parsed.offset ?? 0)
        : undefined;
    }
    const element =
      (id === undefined ? null : doc.getElementById(id)) ??
      Array.from(node.childNodes)
        .filter(isElement)
        .at(step / ELEMENT_STEP_DIVISOR - 1);
    if (!element) return undefined;
    node = element;
  }
  return firstTextPoint(node) ?? { node, offset: 0 };
}

function characterRange(doc: Document, point: CfiPoint): Range {
  const range = doc.createRange();
  if (!isText(point.node)) {
    range.selectNode(point.node);
    return range;
  }
  const { length } = point.node;
  const start = Math.max(0, Math.min(point.offset, length - 1));
  range.setStart(point.node, start);
  range.setEnd(point.node, Math.min(start + 1, length));
  return range;
}

/** Whether a stored `selection.percent` is a legacy CFI rather than a numeric progression. */
export function isLegacyCfi(value: string): boolean {
  return CFI_START.test(value.trim());
}

/**
 * Resolves a legacy content CFI against the chapter document it was created on. A point CFI
 * yields a one-character range (so it has client rects to test for visibility), a range CFI
 * yields the range it describes. Returns `undefined` when the CFI does not fit the document.
 */
export function resolveContentCfi(doc: Document, cfi: string): Range | undefined {
  const content = contentPart(cfi.trim());
  const parts = content.split(',');

  if (parts.length === RANGE_PARTS) {
    const [parent, start, end] = parts;
    const startPoint = resolvePath(doc, `${parent}${start}`);
    const endPoint = resolvePath(doc, `${parent}${end}`);
    if (!startPoint || !endPoint) return undefined;
    const range = doc.createRange();
    range.setStart(startPoint.node, startPoint.offset);
    range.setEnd(endPoint.node, endPoint.offset);
    return range;
  }
  if (parts.length !== 1) return undefined;

  const point = resolvePath(doc, content);
  return point ? characterRange(doc, point) : undefined;
}
